import { useState, useCallback, useRef } from "react";
import { supabase } from "@/products/shared/services/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/products/shared/state/auth-context";
import { Database } from "@/products/shared/services/supabase/types";

// Use the database alert type with minimal UI extensions
type Alert = Database["public"]["Tables"]["alerts"]["Row"] & {
  expanded: boolean;
  recipient_emails: string[];
};

// Raw alert from database procedure - reflects actual return types from get_alerts_for_person.
// Note: `body` is deliberately absent - it is fetched per-page via fetchAlertDetails.
interface RawAlert {
  alert_id: number;
  alert_date: string;
  company: string | null;
  company_id: number | null; // This is actually returned as bigint from DB
  sender: string | null;
  tags: string[];
  assignee: string | null;
  client_status: string | null;
  thread_id: string | null;
  is_company_alert: boolean;
  status: string | null;
  recipient_emails?: string[];
}

export const useAlerts = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const lastFetchRef = useRef<number>(0);
  const fetchPromiseRef = useRef<Promise<Alert[]> | null>(null);
  // The debounce and in-flight dedupe below are only valid for an identical
  // search term, so track which term the cached result belongs to.
  const lastSearchRef = useRef<string>("");

  const transformAlert = (rawAlert: RawAlert | Record<string, unknown>): Alert => ({
    id: Number(rawAlert.alert_id),
    date: String(rawAlert.alert_date),
    company: rawAlert.company as string | null,
    company_id: rawAlert.company_id as number,
    body: null, // populated on demand by fetchAlertDetails
    sender: rawAlert.sender as string | null,
    tags: Array.isArray(rawAlert.tags) ? rawAlert.tags as string[] : null,
    assignee: rawAlert.assignee as string | null,
    status: (rawAlert.status as string) || null,
    client_status: rawAlert.client_status as string | null,
    thread_id: rawAlert.thread_id as string | null,
    is_company_alert: Boolean(rawAlert.is_company_alert),
    old_company_id: null,
    email_id: null,
    ai_approved: null,
    approved: null,
    is_sent: false,
    expanded: false,
    recipient_emails: Array.isArray(rawAlert.recipient_emails) ? rawAlert.recipient_emails as string[] : [],
  });

  const fetchAlerts = useCallback(async (search: string = ""): Promise<Alert[]> => {
    if (!user) {
      console.warn('[useAlerts] No user found, skipping fetch');
      return [];
    }

    // Search is applied server-side, so a changed term must always re-fetch.
    const sameSearch = search === lastSearchRef.current;
    const now = Date.now();
    // Debounce fetches within 2 seconds
    if (sameSearch && now - lastFetchRef.current < 2000 && alerts.length > 0) {
      return alerts;
    }

    // If there's already a fetch in progress for this term, return that promise
    if (sameSearch && fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    setLoading(true);

    try {
      const fetchPromise = (async () => {
        const { data: alertsData, error } = await supabase
          .rpc('get_alerts_for_person', {
            p_person_id: user.id,
            p_search: search || null,
          });

        if (error) {
          throw error;
        }

        if (!alertsData) {
          console.warn('[useAlerts] No alerts data received');
          return [];
        }

        const transformedAlerts = alertsData.map(transformAlert);

        setAlerts(transformedAlerts);
        lastFetchRef.current = now;
        lastSearchRef.current = search;
        return transformedAlerts;
      })();

      fetchPromiseRef.current = fetchPromise;
      const result = await fetchPromise;
      fetchPromiseRef.current = null;
      return result;
    } catch (error) {
      console.error('[useAlerts] Error in fetchAlerts:', error);
      setError('Failed to load alerts');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Fetch bodies for a bounded set of alerts - the page currently on screen.
  // Kept out of the `alerts` state so merging a body cannot clobber per-row UI
  // state (e.g. `expanded`) held by the caller.
  const fetchAlertDetails = useCallback(async (
    alertIds: number[]
  ): Promise<Record<number, string | null>> => {
    if (!user || alertIds.length === 0) return {};

    try {
      const { data, error } = await supabase
        .rpc('get_alert_details', { p_person_id: user.id, p_alert_ids: alertIds });

      if (error) throw error;

      return Object.fromEntries(
        (data ?? []).map(row => [Number(row.alert_id), row.body as string | null])
      );
    } catch (error) {
      console.error('[useAlerts] Error in fetchAlertDetails:', error);
      return {};
    }
  }, [user]);

  // Helper function to process alert data
  const _processAlertData = (data: RawAlert[]): Alert[] => {
    return data.map((alert: RawAlert) => {
      const transformedAlert: Alert = {
        id: alert.alert_id,
        date: alert.alert_date,
        company: alert.company,
        company_id: alert.company_id || 0,
        body: null,
        sender: alert.sender,
        tags: Array.isArray(alert.tags) ? alert.tags : null,
        status: alert.status,
        assignee: alert.assignee,
        client_status: alert.client_status,
        thread_id: alert.thread_id,
        is_company_alert: alert.is_company_alert,
        old_company_id: null,
        email_id: null,
        ai_approved: null,
        approved: null,
        is_sent: false,
        expanded: false,
        recipient_emails: Array.isArray(alert.recipient_emails) ? alert.recipient_emails : [],
      };
      
      return transformedAlert;
    });
  };

  const updateAlertStatus = useCallback(async (id: number, newStatus: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('alerts')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) {
        console.error('[useAlerts] Error updating alert status:', error);
        return false;
      }

      // Update local state
      setAlerts(prevAlerts => 
        prevAlerts.map(alert => 
          alert.id === id ? { ...alert, status: newStatus } : alert
        )
      );

      return true;
    } catch (error) {
      console.error('[useAlerts] Error in updateAlertStatus:', error);
      toast.error('Failed to update alert status');
      return false;
    }
  }, [user]);

  const updateAlertStatusByEmailId = useCallback(async (emailId: string, newStatus: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Keep emailId as string since emails.id is text, not numeric
      const { error } = await supabase
        .from('alerts')
        .update({ status: newStatus })
        .eq('email_id', emailId);

      if (error) {
        console.error('[useAlerts] Error updating alert status by email:', error);
        return false;
      }

      // Update local state
      setAlerts(prevAlerts => 
        prevAlerts.map(alert => 
          alert.email_id === emailId ? { ...alert, status: newStatus } : alert
        )
      );

      return true;
    } catch (error) {
      console.error('[useAlerts] Error in updateAlertStatusByEmailId:', error);
      toast.error('Failed to update alert status');
      return false;
    }
  }, [user]);

  const updateAlertAssignee = useCallback(async (id: number, newAssignee: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('alerts')
        .update({ assignee: newAssignee })
        .eq('id', id);

      if (error) {
        console.error('[useAlerts] Error updating alert assignee:', error);
        return false;
      }

      // Update local state
      setAlerts(prevAlerts =>
        prevAlerts.map(alert =>
          alert.id === id ? { ...alert, assignee: newAssignee } : alert
        )
      );

      return true;
    } catch (error) {
      console.error('[useAlerts] Error in updateAlertAssignee:', error);
      toast.error('Failed to update alert assignee');
      return false;
    }
  }, [user]);

  const getAlertStatusCounts = (alertsToCount: Alert[] = alerts) => {
    const counts = {
      unread: 0,
      investigation: 0,
      acknowledged: 0,
      contacted: 0,
      specialist: 0,
      department: 0,
      'not-applicable': 0,
      win: 0,
    };
    
    alertsToCount.forEach(alert => {
      if (!alert.status) return;
      
      const status = alert.status.toLowerCase();
      if (status === 'unread') {
        counts.unread++;
      } else if (status === 'in progress') {
        counts.investigation++;
      } else if (status === 'read') {
        counts.acknowledged++;
      } else if (status === 'client contacted') {
        counts.contacted++;
      } else if (status === 'resolved') {
        counts.win++;
      } else if (status === 'not applicable') {
        counts['not-applicable']++;
      }
      // Add other status mappings as needed
    });
    
    return counts;
  };

  const tryParseJSON = (jsonString: string, fallback: unknown = null): unknown => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse JSON:', jsonString);
      console.error('Parse error details:', e);
      return fallback;
    }
  };

  return {
    alerts,
    loading: loading || authLoading,
    error,
    fetchAlerts,
    fetchAlertDetails,
    updateAlertStatus,
    updateAlertStatusByEmailId,
    updateAlertAssignee,
    getAlertStatusCounts,
    tryParseJSON
  };
}; 